/**
 * Zones API Service
 * 
 * Provides access to safety zone data with full CRUD and push capabilities
 */

import { api } from './api';

export interface Zone {
  id: string;
  zone_id: string;
  name: string;
  type: 'work_area' | 'vehicle_zone' | 'loading_area' | 'restricted' | 'aisle' | 'emergency_exit';
  zone_type: string;  // API returns this
  enabled?: boolean;
  max_occupancy: number;
  ppe_required?: string[];  // Deprecated; PPE from policies
  restricted: boolean;
  zone_class?: 'general' | 'high_risk' | 'restricted';
  risk_level?: 'low' | 'medium' | 'high' | 'critical' | null;
  must_be_clear?: boolean;
  camera_id?: string;
  device_id?: string;
  bbox_norm?: number[];
  polygon_norm?: number[][];
  color?: string;
  severity?: string;
  alerts?: string[];

  // Phase A policy/timing knobs (stored on zone, used by edge runtime)
  min_dwell_seconds?: number;
  cooldown_seconds?: number;
  persistence_seconds?: number;
  dwell_alert_seconds?: number;
  allowed_entity_types?: string[];
  behavior_type?: string;

  // Notification cadence (cloud-side) for ticketing/digests
  notification_cadence?: 'realtime' | 'daily' | 'weekly' | 'none';
}

export interface ZoneStatus {
  zone_id: string;
  name: string;
  type: string;
  status: 'normal' | 'warning' | 'overcrowded' | 'blocked' | 'alert' | 'unknown';
  occupancy: number | null;
  max_occupancy: number;
  occupancy_percent: number;
  active_alerts: number;
  restricted: boolean;
  ppe_required?: string[];  // Deprecated; PPE from policies
  device_id?: string;
  camera_id?: string;
  timestamp: string;
}

export interface ZoneEvent {
  type: string;
  topic: string;
  timestamp: string;
  data: Record<string, any>;
  device_id: string;
}

export interface ZoneCreateData {
  zone_id: string;
  name: string;
  device_id: string;
  camera_id: string;
  bbox_norm: number[];
  polygon_norm?: number[][];
  color?: string;
  zone_type?: string;
  type?: string;  // Alias for zone_type
  restricted?: boolean;
  zone_class?: 'general' | 'high_risk' | 'restricted';
  risk_level?: 'low' | 'medium' | 'high' | 'critical' | null;
  enabled?: boolean;
  alerts?: string[];
  severity?: string;
  max_occupancy?: number;

  // Phase A policy/timing knobs (optional)
  min_dwell_seconds?: number;
  cooldown_seconds?: number;
  persistence_seconds?: number;
  dwell_alert_seconds?: number;
  allowed_entity_types?: string[];
  behavior_type?: string;

  // Notification cadence (optional; defaults to realtime)
  notification_cadence?: 'realtime' | 'daily' | 'weekly' | 'none';
}

// API Functions
export const zonesApi = {
  /**
   * List all zones
   */
  listZones: async (
    type?: string,
    restricted?: boolean,
    deviceId?: string,
    cameraId?: string,
    includeDisabled?: boolean,
    moduleScope?: 'ocr' | 'warehouse' | 'shared',
    orgId?: string,
    siteId?: string
  ): Promise<{ zones: Zone[]; count: number }> => {
    const params: Record<string, any> = {};
    if (type) params.type = type;
    if (restricted !== undefined) params.restricted = restricted;
    if (deviceId) params.device_id = deviceId;
    if (cameraId) params.camera_id = cameraId;
    if (includeDisabled !== undefined) params.include_disabled = includeDisabled;
    if (moduleScope) params.module_scope = moduleScope;
    if (orgId) params.org_id = orgId;
    if (siteId) params.site_id = siteId;
    const response = await api.get('/api/v1/zones', { params });
    return response.data;
  },

  /**
   * Get zone by ID
   */
  getZone: async (zoneId: string): Promise<Zone> => {
    const response = await api.get(`/api/v1/zones/${zoneId}`);
    return response.data;
  },

  /**
   * Create a new zone
   */
  createZone: async (data: ZoneCreateData): Promise<Zone> => {
    const response = await api.post('/api/v1/zones', data);
    return response.data;
  },

  /**
   * Update an existing zone
   */
  updateZone: async (zoneId: string, data: Partial<ZoneCreateData>): Promise<Zone> => {
    const response = await api.put(`/api/v1/zones/${zoneId}`, data);
    return response.data;
  },

  /**
   * Delete a zone (soft delete by disabling).
   * Pass orgId/siteId to ensure correct scope when backend resolves from headers/params.
   */
  deleteZone: async (
    zoneId: string,
    orgId?: string,
    siteId?: string
  ): Promise<void> => {
    const params: Record<string, string> = {};
    if (orgId) params.org_id = orgId;
    if (siteId) params.site_id = siteId;
    await api.delete(`/api/v1/zones/${encodeURIComponent(zoneId)}`, { params });
  },

  /**
   * Push zones to edge device
   */
  pushToEdge: async (deviceId: string, cameraId?: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/api/v1/zones/push', {
      device_id: deviceId,
      camera_id: cameraId,
    });
    return response.data;
  },

  /**
   * Get zone status (real-time)
   */
  getZoneStatus: async (zoneId: string): Promise<ZoneStatus> => {
    const response = await api.get(`/api/v1/zones/${zoneId}/status`);
    return response.data;
  },

  /**
   * Get all zones status.
   * Pass orgId/siteId to ensure correct scope when headers may be unset (e.g. scope source 'none').
   */
  getAllZonesStatus: async (params?: {
    orgId?: string;
    siteId?: string;
    deviceId?: string;
    cameraId?: string;
  }): Promise<{ zones: ZoneStatus[]; count: number; timestamp: string }> => {
    const queryParams: Record<string, string> = {};
    if (params?.orgId) queryParams.org_id = params.orgId;
    if (params?.siteId) queryParams.site_id = params.siteId;
    if (params?.deviceId) queryParams.device_id = params.deviceId;
    if (params?.cameraId) queryParams.camera_id = params.cameraId;
    const response = await api.get('/api/v1/zones/status', { params: queryParams });
    return response.data;
  },

  /**
   * Get zone occupancy history
   */
  getZoneHistory: async (
    zoneId: string,
    start?: string,
    end?: string,
    step: string = '5m'
  ) => {
    const params: Record<string, any> = { step };
    if (start) params.start = start;
    if (end) params.end = end;
    const response = await api.get(`/api/v1/zones/${zoneId}/history`, { params });
    return response.data;
  },

  /**
   * Get zone incidents
   */
  getZoneIncidents: async (zoneId: string, limit: number = 50) => {
    const response = await api.get(`/api/v1/zones/${zoneId}/incidents`, { params: { limit } });
    return response.data;
  },

  /**
   * Get recent zone events
   */
  getRecentEvents: async (
    zone?: string,
    type?: string,
    limit: number = 100
  ): Promise<{ events: ZoneEvent[]; count: number }> => {
    const params: Record<string, any> = { limit };
    if (zone) params.zone = zone;
    if (type) params.type = type;
    const response = await api.get('/api/v1/zones/events/recent', { params });
    return response.data;
  },

  /**
   * Phase D1: validate zones (deployment-correlated API)
   */
  validateZones: async (
    siteId: string,
    payload: { org_id?: string; edge_device_id?: string; camera_id?: string; zone_ids?: string[] }
  ): Promise<any> => {
    const response = await api.post(`/api/v1/sites/${siteId}/zones/validate`, payload);
    return response.data;
  },

  /**
   * Phase D1: deploy zones (deployment-correlated API)
   */
  deployZones: async (
    siteId: string,
    payload: { org_id?: string; edge_device_id?: string; camera_ids?: string[]; zone_ids?: string[]; initiated_by?: string }
  ): Promise<any> => {
    const response = await api.post(`/api/v1/sites/${siteId}/zones/deploy`, payload);
    return response.data;
  },

  /**
   * Phase D1: preview compiled zone bundles (read-only)
   */
  getZonesBundle: async (
    siteId: string,
    params?: { org_id?: string; edge_device_id?: string; camera_id?: string }
  ): Promise<any> => {
    const response = await api.get(`/api/v1/sites/${siteId}/zones/bundle`, { params: params || {} });
    return response.data;
  },
};

export default zonesApi;
